/* eslint-disable default-case */
import React from 'react';
import Html from 'slate-html-serializer';
import getStyleObject from './getStyleObject';
import { MARKS, NODES } from '../../constants';

const BLOCK_TAGS = {
  p: NODES.PARAGRAPH,
  li: NODES.LIST_ITEM,
  ul: NODES.UNORDERED_LIST,
  ol: NODES.ORDERED_LIST,
  table: NODES.TABLE,
  tr: NODES.TABLE_ROW,
  td: NODES.TABLE_CELL,
  div: NODES.DIV,
  h2: NODES.HEADING_2,
  h3: NODES.HEADING_3,
  h4: NODES.HEADING_4,
  h5: NODES.HEADING_5,
  h6: NODES.HEADING_6,
};

const MARK_TAGS = {
  strong: MARKS.BOLD,
  em: MARKS.ITALIC,
  u: MARKS.UNDERLINE,
};

const RULES = [
  {
    serialize(obj, children) {
      if (obj.object === 'block') {
        switch (obj.type) {
          case NODES.PARAGRAPH:
            return <p>{children}</p>;
          case NODES.PARAGRAPH_LEFT:
            return <p style={{ textAlign: 'left' }}>{children}</p>;
          case NODES.PARAGRAPH_CENTER:
            return <p style={{ textAlign: 'center' }}>{children}</p>;
          case NODES.PARAGRAPH_RIGHT:
            return <p style={{ textAlign: 'right' }}>{children}</p>;
          case NODES.PARAGRAPH_JUSTIFY:
            return <p style={{ textAlign: 'justify' }}>{children}</p>;
          case NODES.LINK:
            return (
              <a
                target={obj.data.get('target')}
                href={obj.data.get('href')}
                data-event-name={obj.data.get('eventName')}>
                {children}
              </a>
            );
          case NODES.ORDERED_LIST:
            return <ol>{children}</ol>;
          case NODES.UNORDERED_LIST:
            return <ul>{children}</ul>;
          case NODES.LIST_ITEM:
            return <li>{children}</li>;
          case NODES.IMAGE:
            return (
              <img
                src={obj.data.get('src')}
                alt={obj.data.get('alt')}
                style={
                  obj.data.get('style') ? obj.data.get('style').toJS() : {}
                }
              />
            );
          case NODES.TABLE:
            return <table>{children}</table>;
          case NODES.TABLE_ROW:
            return <tr>{children}</tr>;
          case NODES.TABLE_CELL:
            return <td>{children}</td>;
          case NODES.VIDEO:
            return (
              <div
                className="embed_wrapper"
                dangerouslySetInnerHTML={{ __html: obj.data.get('embedText') }}
              />
            );
          case NODES.SNIPPET:
            return (
              <div
                className="snippet_block"
                dangerouslySetInnerHTML={{
                  __html: obj.data.get('snippetName'),
                }}
              />
            );
          case NODES.DIV:
            return <div>{children}</div>;
          case NODES.HEADING_2:
            return <h2>{children}</h2>;
          case NODES.HEADING_3:
            return <h3>{children}</h3>;
          case NODES.HEADING_4:
            return <h4>{children}</h4>;
          case NODES.HEADING_5:
            return <h5>{children}</h5>;
          case NODES.HEADING_6:
            return <h6>{children}</h6>;
          case 'paragraph':
            // handle undefined elements
            return null;
        }
      }
    },
  },
  {
    serialize(obj, children) {
      if (obj.object === 'mark') {
        switch (obj.type) {
          case MARKS.BOLD:
            return <strong>{children}</strong>;
          case MARKS.ITALIC:
            return <em>{children}</em>;
          case MARKS.UNDERLINE:
            return <u>{children}</u>;
          case MARKS.FONT_SIZE:
            return (
              <span style={{ fontSize: obj.data.get('size') }}>{children}</span>
            );
          case MARKS.LINE_HEIGHT:
            return (
              <span style={{ lineHeight: obj.data.get('lineHeight') }}>
                {children}
              </span>
            );
          case MARKS.FONT_COLOR:
            return (
              <span style={{ color: obj.data.get('color') }}>{children}</span>
            );
        }
      }
    },
  },
  {
    serialize(obj, children) {
      if (obj.object === 'inline') {
        switch (obj.type) {
          case NODES.LINK:
            return (
              <a
                target={obj.data.get('target')}
                href={obj.data.get('href')}
                data-event-name={obj.data.get('eventName')}>
                {children}
              </a>
            );
          case NODES.TRANSLATION:
            return (
              <span
                className="translation_key"
                dangerouslySetInnerHTML={{
                  __html: obj.data.get('translationKey'),
                }}
              />
            );
          case NODES.SNIPPET_BUTTON:
            return (
              <a
                className="snippet_button"
                href={obj.data.get('href')}
                data-event-name={obj.data.get('eventName')}>
                {obj.data.get('label')}
              </a>
            );
          case NODES.SPAN:
            return <span>{children}</span>;
        }
      }
    },
  },
  {
    deserialize(el, next) {
      if (el.tagName.toLowerCase() === 'p') {
        const slateObject = {
          object: 'block',
          nodes: next(el.childNodes),
          type: NODES.PARAGRAPH,
        };
        switch (el.style.textAlign) {
          case 'left':
            slateObject.type = NODES.PARAGRAPH_LEFT;
            break;
          case 'right':
            slateObject.type = NODES.PARAGRAPH_RIGHT;
            break;
          case 'center':
            slateObject.type = NODES.PARAGRAPH_CENTER;
            break;
          case 'justify':
            slateObject.type = NODES.PARAGRAPH_JUSTIFY;
            break;
          default:
            break;
        }
        return slateObject;
      }
    },
  },
  {
    deserialize(el, next) {
      const mark = MARK_TAGS[el.tagName.toLowerCase()];
      if (mark) {
        return {
          object: 'mark',
          type: mark,
          nodes: next(el.childNodes),
        };
      }
    },
  },
  {
    // Special case for images, to grab their src.
    deserialize(el, next) {
      if (el.tagName.toLowerCase() === 'img') {
        return {
          object: 'block',
          type: NODES.IMAGE,
          nodes: next(el.childNodes),
          data: {
            src: el.getAttribute('src'),
            alt: el.getAttribute('alt'),
            style: getStyleObject(el.getAttribute('style')),
          },
        };
      }
    },
  },
  {
    deserialize(el, next) {
      if (
        el.tagName.toLowerCase() === 'div' &&
        el.classList &&
        el.classList.contains('embed_wrapper')
      ) {
        return {
          object: 'block',
          type: NODES.VIDEO,
          nodes: next(el.childNodes),
          data: {
            embedText: el.innerHTML,
          },
        };
      }
    },
  },
  {
    // Special case for links, to grab their href.
    deserialize(el, next) {
      if (
        el.tagName.toLowerCase() === 'a' &&
        !el.classList.contains('snippet_button')
      ) {
        return {
          object: 'inline',
          type: NODES.LINK,
          nodes: next(el.childNodes),
          data: {
            href: el.getAttribute('href'),
            target: el.getAttribute('target'),
            eventName: el.dataset.eventName,
          },
        };
      }
    },
  },
  {
    deserialize(el, next) {
      if (el.tagName.toLowerCase() === 'span' && el.style.color !== '') {
        return {
          object: 'mark',
          type: MARKS.FONT_COLOR,
          nodes: next(el.childNodes),
          data: {
            color: el.style.color,
          },
        };
      }
    },
  },
  {
    deserialize(el, next) {
      if (el.tagName.toLowerCase() === 'span' && el.style.fontSize !== '') {
        return {
          object: 'mark',
          type: MARKS.FONT_SIZE,
          nodes: next(el.childNodes),
          data: {
            size: el.style.fontSize,
          },
        };
      }
    },
  },
  {
    deserialize(el, next) {
      if (el.tagName.toLowerCase() === 'span' && el.style.lineHeight !== '') {
        return {
          object: 'mark',
          type: MARKS.LINE_HEIGHT,
          nodes: next(el.childNodes),
          data: {
            lineHeight: el.style.lineHeight,
          },
        };
      }
    },
  },
  {
    deserialize(el, next) {
      const elementTag = el.tagName.toLowerCase();
      if (
        (elementTag === 'div' || elementTag === 'span') &&
        el.classList &&
        el.classList.contains('snippet_block')
      ) {
        return {
          object: 'block',
          type: NODES.SNIPPET,
          nodes: next(el.childNodes),
          data: {
            snippetName: el.innerHTML,
          },
        };
      }
    },
  },
  {
    deserialize(el, next) {
      if (
        el.tagName.toLowerCase() === 'span' &&
        el.classList.contains('translation_key')
      ) {
        return {
          object: 'inline',
          type: NODES.TRANSLATION,
          nodes: next(el.childNodes),
          data: {
            translationKey: el.innerHTML,
          },
        };
      }
    },
  },
  {
    deserialize(el, next) {
      if (
        el.tagName.toLowerCase() === 'a' &&
        el.classList.contains('snippet_button')
      ) {
        return {
          object: 'inline',
          type: NODES.SNIPPET_BUTTON,
          nodes: next(el.childNodes),
          data: {
            label: el.innerHTML,
            href: el.getAttribute('href'),
            eventName: el.dataset.eventName,
          },
        };
      }
    },
  },
  {
    deserialize(el, next) {
      if (el.tagName.toLowerCase() === 'span') {
        return {
          object: 'inline',
          type: NODES.SPAN,
          nodes: next(el.childNodes),
        };
      }
    },
  },
  {
    deserialize(el, next) {
      const block = BLOCK_TAGS[el.tagName.toLowerCase()];
      if (block) {
        return {
          object: 'block',
          type: block,
          nodes: next(el.childNodes),
        };
      }
    },
  },
];

const serializer = new Html({ rules: RULES });
window.serializer = serializer;
export default  serializer;
